import { polygonArea } from 'd3'
import { ChartComponent } from '..'
import { Limits } from './limits'
import { ColorScaleInterpolate, ColorScaleStep } from '@/utils/colorScale'
import { LineFormat } from './timeSeries'

export type Orientation = 'vertical' | 'horizontal'
export type Line = [number, number | null][]

export function createLineBottomGradient(
  label: string,
  color: string,
  points: Line,
  limits: Limits
): (ChartComponent<'line'> & { area: number })[] {
  const values = points.concat([
    [points[points.length - 1][0], limits.y[0]],
    [points[0][0], limits.y[0]],
    [points[0][0], points[0][1]]
  ])
  const area = polygonArea(values.filter((v) => v[1] !== null) as [number, number][])
  return [
    {
      type: 'line',
      xLimits: limits.x,
      yLimits: limits.y,
      label: label,
      fill: `${color}33`,
      area,
      gradient: {
        fill: {
          type: 'INTERPOLATE',
          orientation: 'vertical',
          cutoffs: [
            { value: limits.y[0], color, opacity: 0 },
            { value: limits.y[1], color, opacity: 0.2 }
          ]
        }
      },
      stroke: 'none',
      values
    }
  ]
  // })
  // .sort((a, b) => b.area - a.area)

  // .map(({ area, ...rest }) => rest)
  // )
}

export function createLineBottomGradients(
  label: string[],
  color: string[],
  points: Line[],
  limits: Limits
): ChartComponent<'line'>[] {
  return points
    .flatMap((p, i) => createLineBottomGradient(label[i], color[i], p, limits))
    .sort((a, b) => b.area - a.area)
}

function toDashArray(style: 'solid' | 'dashed' | 'dotted'): number[] | undefined {
  switch (style) {
    case 'solid':
      return undefined
    case 'dashed':
      return [5, 5]
    case 'dotted':
      return [1, 5]
  }
}

export const createLine = (
  label: string,
  format: LineFormat | undefined,
  points: Line,
  limits: Limits,
  colorScale?: ColorScaleInterpolate | ColorScaleStep,
  highlightable: boolean = false
): ChartComponent<'line'>[] => {
  return [
    {
      type: 'line',
      xLimits: limits.x,
      yLimits: limits.y,
      label: label,
      stroke: format?.color ?? '#000000',
      strokeWidth: format?.width ?? 1,
      dashArray: toDashArray(format?.style ?? 'solid'),
      values: points,
      highlightable,
      gradient: colorScale
        ? {
            stroke: colorScale
          }
        : undefined
    }
  ]
}

export function createLines(
  label: string[],
  formats: LineFormat[],
  points: Line[],
  limits: Limits,
  colorScale?: ColorScaleInterpolate | ColorScaleStep,
  highlightable: boolean = false
): ChartComponent<'line'>[] {
  return points.flatMap((p, i) =>
    createLine(label[i], formats[i], p, limits, colorScale, highlightable)
  )
}

export const createHighlightLine = (
  limits: [number, number],
  orientation: Orientation
): ChartComponent<'highlight-line'>[] => [
  {
    type: 'highlight-line',
    xLimits: orientation === 'vertical' ? limits : [0, 1],
    yLimits: orientation === 'vertical' ? [0, 1] : limits,
    stroke: '#E0E0E0'
  }
]
